import React, { useContext, useMemo, useState } from "react";
import { MoreVertical } from "lucide-react";
import { Button } from "@/modern/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal
} from "@/modern/components/ui/dropdown-menu";
import BryntumContext, { userPreferences } from "../../context/bryntumContext.jsx";
import useViews from "../../hooks/useViews.jsx";
import { useSwivuStore } from "../../hooks/useSwivuStore.ts";
import { VIEW_TYPES } from "../../../other/constants/OtherKeys.jsx";
import { GRANTS } from "../../../other/constants/PrivilegeTypes.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";
import { EDITOR_KEYS } from "../../../other/constants/EditorKeys.jsx";
import { STORE_KEYS } from "../../../other/constants/StoreKeys.jsx";
import { canSaveView, capitalizeText, openSwivuAssistPopup } from "../../../other/utils/utils.jsx";
import { getIsArchivedForBoard } from "../../../other/utils/otherUtils.jsx";
import { exportBoardView } from "../../../network/network.jsx";
import { TASK_BUDGET_FIELDS, SUPPORTED_VIEWS_FOR_EXPORT, SWIVU_ASSIST_VIEW_TYPE_MAP } from "../../../other/constants/OtherKeys.jsx";
import { checkIncentivePermission, getOutcomeIncentiveMenuItems } from "../../../other/utils/incentiveUtils.jsx";
import { INCENTIVE_PERMISSION_KEYS } from "../../../other/constants/IncentiveKeys.jsx";
import { Mask } from "@bryntum/schedulerpro";
import { DEFAULT_THEME } from "../../../other/constants/TaskTypes.jsx";

const ViewMoreMenu = () => {
  const { project, editorHandler, user, selectedBoard } = useContext(BryntumContext);
  const viewManagerState = useViews();
  const [isOpen, setIsOpen] = useState(false);

  const { version: userPreferenceChanges } = useSwivuStore({
    storeId: STORE_KEYS.userPreferencesStore,
    subscriberFn: ({ record: changedRecord }) =>
      changedRecord?.key === "theme" || changedRecord?.key === PERSISTENCE_STATE_KEYS.taskBudgetFocusMode
  });

  if (!viewManagerState?.selectedView) {
    return null;
  }

  const { selectedView } = viewManagerState;
  const viewType = selectedView.viewType;

  // Generate outcome incentives submenu items dynamically
  const outcomeIncentiveItems = useMemo(() => {
    return getOutcomeIncentiveMenuItems(project, editorHandler, viewType);
  }, [viewType, project, editorHandler, isOpen]);

  // Menu configuration — Save / Save As live in Manage View (header), not here
  const menuConfig = useMemo(() => {
    return [
      {
        key: "manageView",
        type: "item",
        text: "Manage View",
        disabled: false,
        hidden: false,
        onClick: () => {
          if (canSaveView(viewType)) {
            editorHandler.openEditor(EDITOR_KEYS.viewInspectorEditor, null, { viewRecord: selectedView }, { by: "recordType", useExisting: true });
          }
        }
      },
      {
        key: "resetBudgetAnnotations",
        type: "item",
        text: "Reset Budget Annotations",
        disabled: !user?.can(GRANTS.grant_two),
        hidden: viewType !== VIEW_TYPES.taskBudget,
        onClick: () => {
          project.beginBatch();
          project.taskBudgetStore.forEach((record) => {
            if (!(record.isLinked || record.isGroupHeader) && record[TASK_BUDGET_FIELDS.updatedFields].length) {
              record[TASK_BUDGET_FIELDS.updatedFields] = [];
            }
          });
          project.endBatch();
        }
      },
      {
        key: "focusMode",
        type: "checkbox",
        text: "Focus Mode",
        checked: userPreferences?.getSetting(PERSISTENCE_STATE_KEYS.taskBudgetFocusMode) === "true",
        disabled: false,
        hidden: viewType !== VIEW_TYPES.taskBudget || userPreferences.getSetting("theme") === DEFAULT_THEME,
        onChange: (checked) => {
          userPreferences?.updateSetting(PERSISTENCE_STATE_KEYS.taskBudgetFocusMode, String(checked));
        }
      },
      {
        key: "separator1",
        type: "separator",
        hidden: !SWIVU_ASSIST_VIEW_TYPE_MAP[viewType]
      },
      {
        key: "swivuAssist",
        type: "item",
        text: "Show Swivu Assist",
        disabled: false,
        hidden: !SWIVU_ASSIST_VIEW_TYPE_MAP[viewType],
        onClick: () => {
          const swivuAssistanceConfig = SWIVU_ASSIST_VIEW_TYPE_MAP[viewType];
          openSwivuAssistPopup({ editorHandler, swivuAssistanceConfig });
        }
      },
      {
        key: "separator2",
        type: "separator",
        hidden: !SUPPORTED_VIEWS_FOR_EXPORT.includes(viewType)
      },
      {
        key: "exportView",
        type: "item",
        text: `Export ${capitalizeText(viewType) === "Incentive-points" ? "Points" : capitalizeText(viewType)} (CSV)`,
        disabled: false,
        hidden: !SUPPORTED_VIEWS_FOR_EXPORT.includes(viewType),
        onClick: () => {
          Mask.mask({ text: `Exporting ${capitalizeText(viewType)}` });
          const canShowArchived = getIsArchivedForBoard(selectedBoard?.record?.slug);
          exportBoardView(selectedView.id, canShowArchived, selectedView.name).then(() => {
            Mask.unmask();
          });
        }
      },
      {
        key: "separator3",
        type: "separator",
        hidden: viewType !== VIEW_TYPES.incentivePoints
      },
      {
        key: "outcomeIncentives",
        type: "submenu",
        text: "Outcome Points",
        disabled: false,
        hidden: viewType !== VIEW_TYPES.incentivePoints,
        submenu: outcomeIncentiveItems
      },
      {
        key: "pointsLeaderboard",
        type: "item",
        text: "Points Leaderboard",
        disabled: !(
          checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.APPROVED_POINTS_VIEW }) ||
          checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.AWARDED_POINTS_VIEW })
        ),
        hidden: viewType !== VIEW_TYPES.incentivePoints,
        onClick: () => {
          editorHandler.openEditor(EDITOR_KEYS.pointsLeaderboardPopup, {}, {});
        }
      }
    ];
  }, [viewType, selectedView, user, project, editorHandler, selectedBoard, outcomeIncentiveItems, userPreferenceChanges]);

  const renderMenuItem = (config) => {
    if (config.hidden) return null;

    switch (config.type) {
      case "separator":
        return <DropdownMenuSeparator key={config.key} />;

      case "checkbox":
        return (
          <DropdownMenuCheckboxItem
            key={config.key}
            checked={config.checked}
            disabled={config.disabled}
            onCheckedChange={config.onChange}
            className="pl-2 pr-8 [&>span]:left-auto [&>span]:right-2 [&>span]:border [&>span]:border-border [&>span]:rounded-sm"
          >
            {config.text}
          </DropdownMenuCheckboxItem>
        );

      case "submenu":
        if (!config.submenu) return null;
        return (
          <DropdownMenuSub key={config.key}>
            <DropdownMenuSubTrigger disabled={config.disabled}>{config.text}</DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                {config.submenu.active && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger disabled={config.submenu.active.disabled}>{config.submenu.active.text}</DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent>
                        {config.submenu.active.items.map((item) => (
                          <DropdownMenuItem key={item.id} onClick={item.onClick} className={item.archived ? "line-through" : ""}>
                            {item.text}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                )}

                {config.submenu.inactive && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger disabled={config.submenu.inactive.disabled}>{config.submenu.inactive.text}</DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent>
                        {config.submenu.inactive.items.map((item) => (
                          <DropdownMenuItem key={item.id} onClick={item.onClick} className={item.archived ? "line-through" : ""}>
                            {item.text}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                )}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        );

      case "item":
      default:
        return (
          <DropdownMenuItem key={config.key} onClick={config.onClick} disabled={config.disabled}>
            {config.text}
          </DropdownMenuItem>
        );
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <MoreVertical />
          <span className="sr-only">More options</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {menuConfig.map(renderMenuItem)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ViewMoreMenu;